import { getLocalizedString } from "./getLocalizedString.js";

const template = `
	<dialog class="alert-dialog">
			<form method="dialog">
					<article></article>
					<footer>
							<menu></menu>
					</footer>
			</form>
	</dialog>
`;

if (globalThis.document && !document.querySelector("#AlertDialogTemplate")) {
	let $template = document.createElement("template");
	$template.id = "AlertDialogTemplate";
	$template.innerHTML = template;
	document.body.append($template);
}

export function waitForDialog(dialog: HTMLDialogElement, cancelValue = "No"): Promise<string> {
	return new Promise((resolve) => {
		let controller = new AbortController();

		dialog.addEventListener(
			"close",
			() => {
				resolve(dialog.returnValue);
				controller.abort();
			},
			{ signal: controller.signal },
		);

		dialog.addEventListener(
			"cancel",
			() => {
				resolve(cancelValue);
				controller.abort();
			},
			{ signal: controller.signal },
		);
	});
}
export type AlertDialogButton = {
	label: string;
	value: string;
	primary?: boolean;
};

export type AlertDialogOptions = {
	buttons: AlertDialogButton[];
	content: string | HTMLElement;
	className?: string;
	id?: string;
	title?: string;
};

const alertDialogPromiseMap = new Map<string, Promise<string>>();

export async function openAlertDialog({ buttons, content, className, id, title }: AlertDialogOptions) {
	if (id && alertDialogPromiseMap.has(id)) {
		return await alertDialogPromiseMap.get(id);
	}

	let $template = document.querySelector<HTMLTemplateElement>("#AlertDialogTemplate");
	if (!$template) throw Error("No alert dialog template found");

	let $dialog = $template.content.firstElementChild?.cloneNode(true) as HTMLDialogElement | null;
	let $content = $dialog?.querySelector("article");
	let $menu = $dialog?.querySelector("footer menu");

	if (!$dialog || !$content || !$menu) throw Error("Invalid alert dialog template");

	if (className) {
		for (const className_ of className.split(/\s+/)) {
			$dialog?.classList.add(className_);
		}
	}

	if (title) {
		let $header = document.createElement("header");
		$header.append(title);
		$content.parentNode?.insertBefore($header, $content);
	}

	$content.appendChild(typeof content === "string" ? document.createTextNode(content) : content);
	for (let { label, value, primary } of buttons) {
		let listItem = document.createElement("li");
		let buttonElem = document.createElement("button");
		buttonElem.value = value;
		if (primary) {
			buttonElem.classList.add("primary");
		}
		buttonElem.textContent = label;
		listItem.appendChild(buttonElem);
		$menu.appendChild(buttonElem);
	}

	document.body.appendChild($dialog);
	$dialog.showModal();

	let dialogPromise = waitForDialog($dialog).then((returnValue) => {
		if (id && alertDialogPromiseMap.has(id)) {
			alertDialogPromiseMap.delete(id);
		}
		if ($dialog) document.body.removeChild($dialog);
		return returnValue;
	});

	if (id) {
		alertDialogPromiseMap.set(id, dialogPromise);
	}

	return await dialogPromise;
}

export enum ConfirmButtons {
	YesNo = 0,
	OkCancel = 1,
}

export type ConfirmOptions = {
	buttons?: ConfirmButtons;
	title?: string;
};

export async function confirm(prompt: string, options: ConfirmOptions = {}): Promise<boolean> {
	let { buttons: optionButtons = ConfirmButtons.YesNo, title } = options;
	let buttons: AlertDialogButton[] =
		optionButtons === ConfirmButtons.OkCancel
			? [
					{ label: getLocalizedString("OK"), value: "yes", primary: true },
					{ label: getLocalizedString("Cancel"), value: "no" },
				]
			: [
					{ label: getLocalizedString("Yes"), value: "yes", primary: true },
					{ label: getLocalizedString("No"), value: "no" },
				];

	let result = await openAlertDialog({ content: prompt, buttons, className: "alert-dialog-confirm", title });
	return result === "yes";
}

export type AlertOptions = {
	title?: string;
};

export async function alert(prompt: string, options: AlertOptions = {}): Promise<void> {
	await openAlertDialog({
		content: prompt,
		className: "alert-dialog-alert",
		buttons: [{ label: getLocalizedString("OK"), value: "ok", primary: true }],
		...options,
	});
}
